/**
 * EvoHome Improvements — Guided Onboarding dialog.
 *
 * Accessible, keyboard navigable, closable at any point. Onboarding never
 * blocks access to a project, message, quote, document, job or invoice.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, Check, CircleDot, ExternalLink } from "lucide-react";
import { getTour, type OnboardingAudience } from "@/lib/onboarding/tours";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useSetupProgress } from "@/hooks/useSetupProgress";
import { toast } from "@/hooks/use-toast";

interface Props {
  audience: OnboardingAudience;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Start from the persisted step rather than the beginning. */
  resume?: boolean;
}

export default function OnboardingDialog({ audience, open, onOpenChange, resume = true }: Props) {
  const tour = useMemo(() => getTour(audience), [audience]);
  const { state, loading, start, saveStep, complete, skip } = useOnboarding(audience);
  const { tasks, completed, total } = useSetupProgress(open ? audience : null);
  const [index, setIndex] = useState(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const initialised = useRef(false);

  useEffect(() => {
    if (!open) {
      initialised.current = false;
      return;
    }
    // Wait for the persisted state to load, otherwise we would resume at step 1.
    if (loading) return;
    if (initialised.current) return;
    initialised.current = true;
    const startIndex = resume ? Math.min(state.lastStepIndex, tour.steps.length - 1) : 0;
    setIndex(startIndex);
    if (state.status === "not_started") void start();
  }, [open, loading, resume, state.lastStepIndex, state.status, start, tour.steps.length]);


  // Move focus to the step heading on each step change (screen-reader friendly).
  useEffect(() => {
    if (open) headingRef.current?.focus();
  }, [index, open]);

  const step = tour.steps[index];
  const isLast = index === tour.steps.length - 1;
  const stepProgress = Math.round(((index + 1) / tour.steps.length) * 100);

  const goTo = async (next: number) => {
    setIndex(next);
    try {
      await saveStep(next);
    } catch (err) {
      toast({
        title: "Could not save your place",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleFinish = async () => {
    try {
      await complete();
      onOpenChange(false);
      toast({ title: "Guided setup complete", description: "You may reopen it from Help and Tutorials at any time." });
    } catch (err) {
      toast({
        title: "Could not save completion",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSkip = async () => {
    try {
      await skip();
    } catch {
      /* closing must always work, even if saving fails */
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-left">
          <DialogTitle className="text-base md:text-lg">{tour.title}</DialogTitle>
          <DialogDescription className="text-sm">{tour.intro}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Step {index + 1} of {tour.steps.length}
            </span>
            {total > 0 && (
              <span>
                Your setup: {completed} of {total} complete
              </span>
            )}
          </div>
          <Progress
            value={stepProgress}
            aria-label={`Guided setup progress: step ${index + 1} of ${tour.steps.length}`}
          />
        </div>

        <section className="rounded-xl border border-border/60 bg-muted/20 p-4 md:p-5 space-y-3">
          <h2
            ref={headingRef}
            tabIndex={-1}
            className="text-base font-semibold text-foreground outline-none"
          >
            {step.title}
          </h2>
          {step.body.map((paragraph) => (
            <p key={paragraph.slice(0, 24)} className="text-sm text-muted-foreground leading-relaxed">
              {paragraph}
            </p>
          ))}
          {step.points && (
            <ul className="space-y-1.5">
              {step.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CircleDot className="w-3.5 h-3.5 mt-1 text-primary shrink-0" aria-hidden />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          )}
          {step.route && (
            <Button asChild variant="outline" size="sm" className="min-h-11">
              <Link to={step.route} onClick={() => onOpenChange(false)}>
                {step.routeLabel ?? "Open"}
                <ExternalLink className="w-3.5 h-3.5 ml-1.5" aria-hidden />
              </Link>
            </Button>
          )}
          {step.helpSlug && (
            <p className="text-xs text-muted-foreground">
              More detail is available in Help and Tutorials.
            </p>
          )}
        </section>

        {index === 0 && tasks.length > 0 && (
          <section aria-label="Your setup checklist" className="rounded-xl border border-border/60 p-4 space-y-2">
            <h3 className="text-sm font-semibold text-foreground">Your EvoHome Improvements setup</h3>
            <ul className="space-y-1.5">
              {tasks.map((task) => (
                <li key={task.id} className="flex items-start gap-2 text-sm">
                  <span
                    className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border shrink-0 ${
                      task.done ? "bg-primary border-primary" : "border-muted-foreground/40"
                    }`}
                    aria-hidden
                  >
                    {task.done && <Check className="h-3 w-3 text-primary-foreground" />}
                  </span>
                  <span className={task.done ? "text-muted-foreground line-through" : "text-foreground"}>
                    {task.label}
                    <span className="sr-only">{task.done ? " — complete" : " — outstanding"}</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 pt-1">
          <Button variant="ghost" size="sm" className="min-h-11" onClick={handleSkip}>
            Skip for now
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="min-h-11"
              disabled={index === 0}
              onClick={() => void goTo(index - 1)}
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" aria-hidden />
              Back
            </Button>
            {isLast ? (
              <Button size="sm" className="min-h-11" onClick={handleFinish}>
                <Check className="w-4 h-4 mr-1.5" aria-hidden />
                Finish
              </Button>
            ) : (
              <Button size="sm" className="min-h-11" onClick={() => void goTo(index + 1)}>
                Next
                <ArrowRight className="w-4 h-4 ml-1.5" aria-hidden />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
